import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Linking,
  Platform,
} from "react-native";
import { Image } from "expo-image";
import { spacing, typography } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import { Feather, MaterialIcons } from "@expo/vector-icons";
import { useOrders, Order } from "../hooks/useOrders";
import {
  useOrderRequests,
  OrderRequest,
  OrderRequestItem,
} from "../hooks/useOrderRequests";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { format } from "date-fns";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../types/navigation";
import { supabase } from "../lib/supabase";
import { toDisplayLine, currency } from "../helpers/orders";
import { DeliveryTracker } from "../components/DeliveryTracker";
import { DELIVERY_CONFIG, DeliveryHelpers } from "../config/deliveryConfig";
import { formatPrice } from "../utils/format";

type PurchasesScreenNavigationProp = NativeStackNavigationProp<RootStackParamList>;
type OrdersScreenRouteProp = RouteProp<RootStackParamList, "Orders">;

type TabType = "purchases" | "pending";
type OrderItem = OrderRequestItem | OrderRequest;

export default function OrdersScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  const navigation = useNavigation<PurchasesScreenNavigationProp>();
  const route = useRoute<OrdersScreenRouteProp>();
  const {
    orders,
    loading: ordersLoading,
    error: ordersError,
    refetch: refetchOrders,
  } = useOrders();
  const {
    orderRequests,
    loading: requestsLoading,
    error: requestsError,
    refetch: refetchRequests,
  } = useOrderRequests();

  // Support initialTab from navigation params (e.g., "pending" for Sales tab)
  const initialTab = route?.params?.initialTab as TabType | undefined;
  const [activeTab, setActiveTab] = useState<TabType>(initialTab || "purchases");

  // Get and log current user info when component mounts
  useEffect(() => {
    async function checkUserAndAuth() {
      try {
        // Check authentication status
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
          console.error("Session error:", sessionError.message);
          return;
        }

        if (!session) {
          console.log("No active session found");
          return;
        }

        try {
          // Make a raw SQL query to bypass RLS
          const { data: rawResults, error: rawError } = await supabase.rpc(
            "admin_check_order_requests"
          );

          if (rawError) {
            // Try direct table access
            const { data, error } = await supabase
              .from("order_requests")
              .select("id, created_at, user_id")
              .limit(5);

            if (error) {
              console.error(
                "Error querying order_requests directly:",
                error.message
              );
            } else {
              console.log(
                `Found ${data?.length || 0} order_requests (first 5 shown):`,
                data
              );
            }
          } else {
            console.log(`RPC found ${rawResults?.length || 0} order_requests`);
          }
        } catch (sqlError) {
          console.error("SQL error:", sqlError);
        }
      } catch (err) {
        console.error("Error checking auth status:", err);
      }
    }

    checkUserAndAuth();
  }, []);

  // Add this useEffect to log when tabs are switched and trigger a refresh
  useEffect(() => {
    if (activeTab === "pending") {
      refetchRequests();
    } else {
      refetchOrders();
    }
  }, [activeTab, refetchRequests, refetchOrders]);

  // Add a clearer tab switching function
  const switchTab = (tab: TabType) => {
    setActiveTab(tab);
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case "pending":
        return (
          <MaterialIcons name="hourglass-empty" size={24} color="#f59e0b" />
        );
      case "confirmed":
        return <MaterialIcons name="check-circle" size={24} color="#00A86B" />;
      case "paid":
        return <MaterialIcons name="check-circle" size={24} color="#00A86B" />;
      case "wrapping_up":
        return <MaterialIcons name="work" size={24} color="#00D488" />;
      case "packed":
        return <MaterialIcons name="inventory" size={24} color="#00D488" />;
      case "pickup_pending":
        return (
          <MaterialIcons name="delivery-dining" size={24} color="#00D488" />
        );
      case "in_transit":
        return (
          <MaterialIcons name="local-shipping" size={24} color="#00D488" />
        );
      case "completed":
        return <MaterialIcons name="verified" size={24} color="#00A86B" />;
      case "rejected":
        return <MaterialIcons name="cancel" size={24} color="#F43F5E" />;
      case "cancelled":
        return <MaterialIcons name="cancel" size={24} color="#F43F5E" />;
      default:
        return <MaterialIcons name="help-outline" size={24} color="#6b7280" />;
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case "pending":
        return "Pending Approval";
      case "confirmed":
        return "Order Confirmed";
      case "paid":
      case "succeeded":
        return "Waiting for Seller";
      case "wrapping_up":
        return "Wrapping Up";
      case "packed":
        return "Packed";
      case "pickup_pending":
        return "Courier on the Way";
      case "pickup_complete":
        return "Pickup Complete";
      case "in_transit":
        return "In Transit";
      case "delivered":
        return "Delivered";
      case "completed":
        return "Completed";
      case "rejected":
        return "Rejected";
      case "cancelled":
        return "Cancelled";
      case "failed":
        return "Failed";
      default:
        // Convert snake_case to Title Case for unknown statuses
        if (status) {
          return status
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
        }
        return "Processing";
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "pending":
        return "#f59e0b"; // amber/warning
      case "confirmed":
        return "#00A86B"; // jade green
      case "paid":
        return "#00A86B"; // jade green
      case "wrapping_up":
        return "#00D488"; // jade green (bright)
      case "packed":
        return "#00D488"; // jade green (bright)
      case "pickup_pending":
        return "#00D488"; // jade green (bright)
      case "in_transit":
        return "#00D488"; // jade green (bright)
      case "completed":
        return "#00A86B"; // jade green
      case "rejected":
        return "#F43F5E"; // rose red
      case "cancelled":
        return "#F43F5E"; // rose red
      default:
        return "#6b7280"; // gray
    }
  };

  const getDeliveryProgress = (status?: string) => {
    switch (status) {
      case "pending":
        return 10;
      case "confirmed":
        return 20;
      case "paid":
        return 30;
      case "wrapping_up":
        return 40;
      case "packed":
        return 50;
      case "pickup_pending":
        return 70;
      case "in_transit":
        return 90;
      case "completed":
        return 100;
      default:
        return 0;
    }
  };

  const renderOrder = ({ item }: { item: Order }) => {
    const created = item.created_at ? new Date(item.created_at) : new Date();
    // For buyer orders, use the main order status, fallback to delivery_status
    const orderStatus = item.status || item.delivery_status;
    const isOrderActive =
      orderStatus &&
      !["completed", "cancelled", "rejected"].includes(orderStatus);

    const lines = Array.isArray(item.items)
      ? item.items.map(toDisplayLine)
      : [];
    const previewImages = lines
      .map((l) => l.image)
      .filter(Boolean)
      .slice(0, 3) as string[];

    const canTrack =
      !!item.tracking_url &&
      ["pickup_pending", "in_transit"].includes(
        item.delivery_status || item.status || ""
      );

    return (
      <TouchableOpacity
        style={styles.orderCard}
        onPress={() =>
          navigation.navigate("BuyerOrderDetails", { order: item })
        }
      >
        {/* Header */}
        <View style={styles.orderHeader}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <Text style={styles.orderId}>
              Order #{String(item.id).slice(0, 8)}
            </Text>
          </View>
          {typeof item.total_amount === "number" && (
            <View style={styles.badgeTotal}>
              <Text style={styles.badgeTotalText}>
                {currency(item.total_amount)}
              </Text>
            </View>
          )}
        </View>
        <Text style={styles.orderDate}>
          {format(created, "MMM d, yyyy h:mm a")}
        </Text>

        {/* Thumbnails */}
        {previewImages.length > 0 && (
          <View style={styles.thumbRow}>
            {previewImages.map((uri, idx) => (
              <View key={`${item.id}-img-${idx}`} style={styles.thumbWrap}>
                <Image source={{ uri }} style={styles.thumb} />
                {idx === 2 && lines.length > 3 ? (
                  <View style={styles.moreOverlay}>
                    <Text style={styles.moreOverlayText}>
                      +{lines.length - 3}
                    </Text>
                  </View>
                ) : null}
              </View>
            ))}
          </View>
        )}

        {/* Lines */}
        <View style={styles.orderItems}>
          {lines.slice(0, 3).map((l, idx) => (
            <View key={`${item.id}-line-${idx}`} style={styles.itemRow}>
              <View style={{ flex: 1 }}>
                <Text numberOfLines={1} style={styles.itemTitle}>
                  {l.title}
                </Text>
                <View style={styles.itemMetaRow}>
                  <Text style={styles.itemMeta}>
                    {l.qty}× {currency(l.price)}
                  </Text>
                  {!!l.vendorName && (
                    <View style={styles.vendorPill}>
                      <Feather name="home" size={12} color={colors.primary} />
                      <Text numberOfLines={1} style={styles.vendorPillText}>
                        {l.vendorName}
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            </View>
          ))}
        </View>

        {/* Order Progress Bar for Active Orders */}
        {isOrderActive && (
          <View style={styles.progressSection}>
            <View style={styles.progressBar}>
              <View
                style={[
                  styles.progressFill,
                  { width: `${getDeliveryProgress(orderStatus)}%` },
                ]}
              />
            </View>
            <Text style={styles.progressText}>
              {getDeliveryProgress(orderStatus)}% complete
            </Text>
          </View>
        )}

        {/* Footer */}
        <View style={styles.orderFooter}>
          <View style={styles.statusContainer}>
            {getStatusIcon(orderStatus)}
            <Text
              style={[
                styles.statusText,
                { color: getStatusColor(orderStatus) },
                isOrderActive ? styles.activeStatus : null,
              ]}
            >
              {getStatusText(orderStatus)}
            </Text>
          </View>

          {item.tracking_url && canTrack ? (
            <View style={styles.trackCta}>
              <Text style={styles.trackCtaText}>Track</Text>
              <Feather name="external-link" size={16} color={colors.primary} />
            </View>
          ) : null}
        </View>

        {/* Delivery Tracking - Show for active deliveries */}
        {/* {isOrderActive && item.delivery_id && (
          <View style={styles.deliverySection}>
            <DeliveryTracker deliveryId={item.delivery_id} />
          </View>
        )} */}
      </TouchableOpacity>
    );
  };

  const renderOrderRequest = ({ item }: { item: OrderRequest }) => {
    const statusIcon = getStatusIcon(item.status);
    const statusText = getStatusText(item.status);
    const statusColor = getStatusColor(item.status);

    // Defensive formatting
    const total = typeof item.total_amount === "number" ? item.total_amount : 0;
    const subtotal =
      typeof item.sub_total === "number" ? item.sub_total : undefined;
    const fee =
      typeof item.delivery_fee === "number" ? item.delivery_fee : undefined;

    return (
      <TouchableOpacity
        style={styles.orderContainer}
        onPress={() => {
          navigation.navigate("VendorOrderDetails", { vendorOrderId: item.id });
        }}
      >
        {/* Header */}
        <View style={styles.orderHeader}>
          <Text style={styles.orderDate}>
            {format(new Date(item.created_at), "MMM d, yyyy h:mm a")}
          </Text>
          <View style={styles.orderStatus}>
            {statusIcon}
            <Text style={[styles.statusText, { color: statusColor }]}>
              {statusText}
            </Text>
          </View>
        </View>

        {/* Items */}
        <View style={styles.orderItems}>
          {item.items.slice(0, 3).map((line, idx) => (
            <Text key={`${item.id}-line-${idx}`} style={styles.itemText}>
              {line.quantity}× {line.name}
              {typeof line.unit_price === "number"
                ? ` · ${formatPrice(line.unit_price)}`
                : ""}
            </Text>
          ))}
          {item.items.length > 3 && (
            <Text style={styles.moreItemsText}>
              +{item.items.length - 3} more items
            </Text>
          )}
        </View>

        {/* Progress Section - Show for all active statuses */}
        {!["rejected", "cancelled", "completed"].includes(item.status) && (
          <View style={styles.progressSection}>
            <View style={styles.progressBar}>
              <View
                style={[
                  styles.progressFill,
                  { width: `${getDeliveryProgress(item.status)}%` },
                ]}
              />
            </View>
            <Text style={styles.progressText}>
              {getDeliveryProgress(item.status)}% complete
            </Text>
          </View>
        )}

        {/* Money + CTA */}
        <View style={styles.orderFooter}>
          <View>
            {typeof subtotal === "number" ? (
              <>
                <Text style={[styles.itemText, { color: colors.textLight }]}>
                  Items Total: {formatPrice(subtotal)}
                </Text>
                <Text style={[styles.itemText, { color: colors.textLight }]}>
                  Processing Fee: -{formatPrice(DeliveryHelpers.calculateSellerFee(subtotal))}
                </Text>
                <Text style={styles.itemText}>
                  You'll Receive: {formatPrice(subtotal - DeliveryHelpers.calculateSellerFee(subtotal))}
                </Text>
              </>
            ) : (
              <Text style={styles.orderTotal}>Total: {formatPrice(total)}</Text>
            )}
          </View>

          <View style={styles.actionButton}>
            <Text style={styles.actionButtonText}>
              {item.status === "pending"
                ? "View Status"
                : item.status === "confirmed"
                ? "View Order"
                : [
                    "paid",
                    "wrapping_up",
                    "packed",
                    "pickup_pending",
                    "in_transit",
                  ].includes(item.status)
                ? "View Order"
                : "Details"}
            </Text>
            <Feather name="chevron-right" size={18} color={colors.primary} />
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const handleRefresh = () => {
    console.log(`🔄 Manual refresh requested for ${activeTab} tab`);
    if (activeTab === "purchases") {
      refetchOrders();
    } else {
      refetchRequests();
    }
  };

  const isLoading = activeTab === "purchases" ? ordersLoading : requestsLoading;
  const error = activeTab === "purchases" ? ordersError : requestsError;
  const isEmpty =
    activeTab === "purchases"
      ? !orders || orders.length === 0
      : !orderRequests || orderRequests.length === 0;

  const TabHeader = () => (
    <View style={styles.tabContainer}>
      <TouchableOpacity
        style={[
          styles.tabButton,
          activeTab === "purchases" && styles.activeTabButton,
        ]}
        onPress={() => switchTab("purchases")}
      >
        <Feather 
          name="shopping-bag" 
          size={18} 
          color={activeTab === "purchases" ? "#000000" : "#FFFFFF"} 
        />
        <Text
          style={[
            styles.tabText,
            activeTab === "purchases" && styles.activeTabText,
          ]}
        >
          Purchases
        </Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[
          styles.tabButton,
          activeTab === "pending" && styles.activeTabButton,
        ]}
        onPress={() => switchTab("pending")}
      >
        <Feather 
          name="tag" 
          size={18} 
          color={activeTab === "pending" ? "#000000" : "#FFFFFF"} 
        />
        <Text
          style={[
            styles.tabText,
            activeTab === "pending" && styles.activeTabText,
          ]}
        >
          Sales
        </Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={styles.welcomeSection}>
        <Text style={styles.headerTitle}>Orders</Text>
        <Text style={styles.welcomeSubtext}>Track your purchases and sales</Text>
      </View>
      <View style={styles.tabWrapper}>
        <TabHeader />
      </View>

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading {activeTab === "purchases" ? "purchases" : "sales"}...</Text>
        </View>
      ) : error ? (
        <View style={styles.errorContainer}>
          <Feather name="alert-circle" size={64} color={colors.error} />
          <Text style={styles.errorTitle}>
            Unable to Load{" "}
            {activeTab === "purchases" ? "Purchases" : "Sales"}
          </Text>
          <Text style={styles.errorText}>
            {typeof error === "string"
              ? error
              : error?.message || `Failed to load ${activeTab === "purchases" ? "purchases" : "sales"}. Please try again.`}
          </Text>
          <TouchableOpacity style={styles.retryButton} onPress={handleRefresh}>
            <Text style={styles.retryText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      ) : isEmpty ? (
        <View style={styles.emptyContainer}>
          <View style={styles.emptyIconContainer}>
            <Feather
              name={activeTab === "purchases" ? "package" : "clock"}
              size={64}
              color="#FFFFFF"
            />
          </View>
          <Text style={styles.emptyTitle}>
            {activeTab === "purchases" ? "No purchases yet" : "No sales yet"}
          </Text>
          <Text style={styles.emptyText}>
            {activeTab === "purchases"
              ? "Your purchase history will appear here once you make a purchase."
              : "When you list items and receive orders, they will appear here."}
          </Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={handleRefresh}
          >
            <Text style={styles.retryText}>Refresh</Text>
          </TouchableOpacity>
        </View>
      ) : (
        activeTab === "purchases" ? (
          <FlatList<Order>
            data={orders ?? []}
            renderItem={renderOrder}
            keyExtractor={(item) => item.id}
            contentContainerStyle={styles.listContent}
            refreshControl={
              <RefreshControl
                refreshing={isLoading}
                onRefresh={handleRefresh}
                colors={[colors.primary]}
              />
            }
            ListEmptyComponent={() => (
              <View style={styles.emptyContainer}>
                <View style={styles.emptyIconContainer}>
                  <Feather name="package" size={64} color="#FFFFFF" />
                </View>
                <Text style={styles.emptyTitle}>No purchases yet</Text>
                <Text style={styles.emptyText}>
                  Your purchase history will appear here once you make a purchase.
                </Text>
                <TouchableOpacity
                  style={styles.retryButton}
                  onPress={handleRefresh}
                >
                  <Text style={styles.retryText}>Refresh</Text>
                </TouchableOpacity>
              </View>
            )}
          />
        ) : (
          <FlatList<OrderRequest>
            data={orderRequests ?? []}
            renderItem={renderOrderRequest}
            keyExtractor={(item) => item.id}
            contentContainerStyle={styles.listContent}
            refreshControl={
              <RefreshControl
                refreshing={isLoading}
                onRefresh={handleRefresh}
                colors={[colors.primary]}
              />
            }
            ListEmptyComponent={() => (
              <View style={styles.emptyContainer}>
                <View style={styles.emptyIconContainer}>
                  <Feather name="clock" size={64} color="#FFFFFF" />
                </View>
                <Text style={styles.emptyTitle}>No sales yet</Text>
                <Text style={styles.emptyText}>
                  When you list items and receive orders, they will appear here.
                </Text>
                <TouchableOpacity
                  style={styles.retryButton}
                  onPress={handleRefresh}
                >
                  <Text style={styles.retryText}>Refresh</Text>
                </TouchableOpacity>
              </View>
            )}
          />
        )
      )}
    </View>
  );
}

type AnyErr = unknown;

function parseError(err: AnyErr) {
  // Normalizes various error shapes into {title, message, details}
  if (!err)
    return {
      title: "Something went wrong",
      message: "Unknown error",
      details: undefined,
    };

  // Supabase / Postgrest error-ish
  if (typeof err === "object" && err !== null) {
    const e = err as any;
    const title = e.code ? `Error ${e.code}` : "Something went wrong";
    const message =
      e.message ||
      e.error_description ||
      e.error ||
      "An unexpected error occurred.";
    const details = e.details || e.hint || e.reason || e.info || undefined;
    return { title, message: String(message), details };
  }

  if (typeof err === "string") {
    return { title: "Something went wrong", message: err, details: undefined };
  }

  return {
    title: "Something went wrong",
    message: String(err),
    details: undefined,
  };
}

function ErrorCard({
  error,
  onRetry,
  label = "Retry",
}: {
  error: AnyErr;
  onRetry?: () => void;
  label?: string;
}) {
  const [showMore, setShowMore] = React.useState(false);
  const { title, message, details } = parseError(error);
  const colors = useColors();
  const styles = createStyles(colors);

  return (
    <View style={styles.errorWrap}>
      <View style={styles.errorIconCircle}>
        <MaterialIcons name="error-outline" size={22} color={colors.error} />
      </View>
      <View style={{ flex: 1 }}>
        <Text style={styles.errorTitle}>{title}</Text>
        <Text style={styles.errorMessage}>{message}</Text>

        {!!details && (
          <TouchableOpacity onPress={() => setShowMore((s) => !s)}>
            <Text style={styles.errorMore}>
              {showMore ? "Hide details" : "Show details"}
            </Text>
          </TouchableOpacity>
        )}

        {showMore && !!details && (
          <View style={styles.errorDetailsBox}>
            <Text style={styles.errorDetailsText}>{String(details)}</Text>
          </View>
        )}

        {onRetry && (
          <TouchableOpacity style={styles.errorRetryBtn} onPress={onRetry}>
            <Text style={styles.errorRetryText}>{label}</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    welcomeSection: {
      paddingLeft: spacing.lg + 4,
      paddingRight: spacing.lg,
      paddingTop: spacing.sm,
      paddingBottom: spacing.md,
    },
    headerTitle: {
      fontSize: 28,
      fontWeight: "800",
      color: colors.text,
      letterSpacing: -0.5,
    },
    welcomeSubtext: {
      fontSize: 16,
      color: colors.textSecondary,
      marginTop: 2,
      fontWeight: "500",
    },
    tabWrapper: {
      paddingHorizontal: spacing.lg,
      marginBottom: spacing.md,
    },
    tabContainer: {
      flexDirection: "row",
      gap: 10,
    },
    tabButton: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: 18,
      paddingVertical: 10,
      borderRadius: 16,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.05,
      shadowRadius: 4,
      elevation: 2,
      gap: 8,
    },
    activeTabButton: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    tabText: {
      fontSize: 14,
      fontWeight: "500",
      color: "#FFFFFF",
    },
    activeTabText: {
      color: "#000000",
      fontWeight: "600",
    },
    listContent: {
      paddingHorizontal: spacing.lg,
      paddingTop: spacing.xs,
      paddingBottom: spacing.xl,
    },
    orderCard: {
      backgroundColor: colors.surface,
      borderRadius: 20,
      padding: spacing.md,
      marginBottom: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.03,
      shadowRadius: 8,
      elevation: 2,
    },
    orderContainer: {
      backgroundColor: colors.surface,
      borderRadius: 20,
      padding: spacing.md,
      marginBottom: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.03,
      shadowRadius: 8,
      elevation: 2,
    },
    emptyContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing.xl,
      marginTop: -spacing.xxl,
    },
    emptyIconContainer: {
      width: 120,
      height: 120,
      borderRadius: 60,
      backgroundColor: "#000000",
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: spacing.lg,
    },
    emptyTitle: {
      fontSize: 22,
      fontWeight: "700",
      color: colors.text,
      marginBottom: spacing.xs,
      textAlign: 'center',
    },
    emptyText: {
      fontSize: 16,
      color: colors.textLight,
      textAlign: 'center',
      lineHeight: 22,
      paddingHorizontal: spacing.md,
      marginBottom: spacing.xl,
    },
    retryButton: {
      backgroundColor: colors.primary,
      paddingHorizontal: spacing.xl,
      paddingVertical: 16,
      borderRadius: 28,
      shadowColor: colors.primary,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 8,
      elevation: 4,
    },
    retryText: {
      color: "#000000",
      fontSize: 16,
      fontWeight: "700",
    },
    orderHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.xs,
    },
    orderId: {
      fontSize: 16,
      fontWeight: "700",
      color: colors.text,
    },
    orderDate: {
      fontSize: 13,
      color: colors.textLight,
      marginBottom: spacing.md,
    },
    orderItems: {
      marginBottom: spacing.md,
    },
    itemRow: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.xs,
    },
    itemTitle: {
      fontSize: 15,
      fontWeight: "600",
      color: colors.text,
    },
    itemMetaRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      marginTop: 2,
    },
    itemMeta: {
      fontSize: 13,
      color: colors.textLight,
    },
    itemText: {
      fontSize: 14,
      color: colors.text,
      marginBottom: 4,
    },
    orderFooter: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: spacing.md,
      marginTop: spacing.xs,
    },
    statusContainer: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.xs,
    },
    statusText: {
      fontSize: 14,
      fontWeight: "600",
    },
    activeStatus: {
      fontWeight: "700",
    },
    orderStatus: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.xs,
    },
    orderTotal: {
      fontSize: 16,
      fontWeight: "700",
      color: colors.text,
    },
    actionButton: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.xs,
    },
    actionButtonText: {
      fontSize: 14,
      color: colors.primary,
      fontWeight: "700",
    },
    badgeTotal: {
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 8,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    badgeTotalText: {
      fontSize: 14,
      fontWeight: "700",
      color: colors.text,
    },
    vendorPill: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 999,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    vendorPillText: {
      fontSize: 11,
      color: colors.primary,
      fontWeight: "600",
    },
    thumbRow: {
      flexDirection: "row",
      gap: 10,
      marginBottom: spacing.md,
    },
    thumbWrap: {
      width: 60,
      height: 60,
      borderRadius: 12,
      overflow: "hidden",
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    thumb: {
      width: "100%",
      height: "100%",
    },
    moreOverlay: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: "rgba(0,0,0,0.5)",
      alignItems: "center",
      justifyContent: "center",
    },
    moreOverlayText: {
      color: "#fff",
      fontWeight: "700",
      fontSize: 14,
    },
    progressSection: {
      marginBottom: spacing.md,
      marginTop: spacing.xs,
    },
    progressBar: {
      height: 6,
      backgroundColor: colors.surface,
      borderRadius: 3,
      marginBottom: 6,
      overflow: "hidden",
      borderWidth: 1,
      borderColor: colors.border,
    },
    progressFill: {
      height: "100%",
      backgroundColor: colors.primary,
    },
    progressText: {
      fontSize: 12,
      color: colors.textSecondary,
      fontWeight: "600",
    },
    trackCta: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      backgroundColor: colors.primary + '10',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 12,
    },
    trackCtaText: {
      color: colors.primary,
      fontWeight: "700",
      fontSize: 13,
    },
    loadingContainer: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
    },
    loadingText: {
      fontSize: 16,
      color: colors.textLight,
      marginTop: spacing.md,
    },
    errorContainer: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.xl,
    },
    errorTitle: {
      fontSize: 20,
      fontWeight: "700",
      color: colors.text,
      marginTop: spacing.lg,
      marginBottom: spacing.xs,
    },
    errorText: {
      fontSize: 16,
      color: colors.textSecondary,
      textAlign: "center",
      marginBottom: spacing.xl,
    },
    errorWrap: {
      flexDirection: "row",
      gap: 12,
      padding: 16,
      marginHorizontal: spacing.lg,
      marginTop: spacing.lg,
      borderRadius: 16,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    errorIconCircle: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: colors.errorBackground,
      alignItems: "center",
      justifyContent: "center",
    },
    errorMessage: {
      fontSize: 14,
      color: colors.error,
    },
    errorMore: {
      color: colors.primary,
      fontWeight: "600",
      marginTop: 8,
    },
    errorDetailsBox: {
      marginTop: 8,
      padding: 12,
      backgroundColor: colors.background,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
    },
    errorDetailsText: {
      fontSize: 13,
      color: colors.textSecondary,
      fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
    },
    errorRetryBtn: {
      marginTop: 12,
      backgroundColor: colors.primary,
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 12,
    },
    errorRetryText: {
      color: "#000",
      fontWeight: "700",
    },
    moreItemsText: {
      fontSize: 13,
      color: colors.textLight,
      marginTop: 4,
      fontStyle: 'italic',
    },
  });
